import { useEffect, useMemo, useState } from 'react';
import { ProgressCard } from 'design-system/src/components/ProgressCard/ProgressCard';
import { Input } from '../Input';
import Button from 'design-system/src/components/Button/Button';
import { CardContent, CardSection, CardSubSection, START_STEP_DESKTOP_MIN_HEIGHT } from './styles';
import { EMAIL_VALIDATION_REGEX } from '../../constants';
import { Controller, useForm } from 'react-hook-form';
import { ErrorMessage } from 'design-system/src/components/ErrorMessage/ErrorMessage';
import { SignupErrorMessage } from './SignupErrorMessage';
import { requiredErrorString } from '../../utils';
import SignupFlowContent from './SignupFlowContent';
import { SignupFormData } from './types';
import { createBeforeUnloadHandler } from './utils.tsx';
import { PageContainerWithLogo } from './PageContainerWithLogo.tsx';
import { client } from '../../api';
import { useMutation } from '@tanstack/react-query';
import { handleServerError } from '../../utils';
import CertificationIcons from './CertificationIcons';
import { createTrackingProps } from 'shared-utils/analytics';
import { createPartnerStackSignup } from '../../utils/partnerStack';
import { UtmParams } from '../../types';
import { submitHubspotForm } from '../../utils/conversionTracking';

export type SignupFlowStartProps = {
  className?: string;
  onNext: (skipToVerificationEmail?: boolean) => void;
  isDemoFlow?: boolean;
  onBeforeUnload?: (formData: SignupFormData) => void;
  setSignupId: (signupId: string) => void;
  setEmail: (email: string) => void;
  defaultEmail?: string;
  utmParams?: UtmParams;
  googleAdClickId?: string;
};

type SignupFlowStartResponse = { signup_id: string } & Partial<{ booked_demo: boolean; triaged: boolean }>;

export const SignupFlowStart = ({
  className,
  onNext,
  isDemoFlow,
  onBeforeUnload,
  setSignupId,
  setEmail,
  defaultEmail,
  utmParams,
  googleAdClickId,
}: SignupFlowStartProps) => {
  const {
    handleSubmit,
    control,
    formState: { errors },
    getValues,
    clearErrors,
  } = useForm<SignupFormData>({
    defaultValues: {
      email: defaultEmail || '',
    },
  });
  const [error, setError] = useState<string | null>(null);

  const handleBeforeUnload = useMemo(
    () => createBeforeUnloadHandler(onBeforeUnload, getValues),
    [onBeforeUnload, getValues],
  );

  useEffect(() => {
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [handleBeforeUnload]);

  const submitSignup = useMutation<SignupFlowStartResponse, unknown, SignupFormData>({
    mutationFn: async (newFormData: SignupFormData) => {
      const response = await client.post('/signup/submit-signup-flow-start', {
        email: newFormData.email,
        source: isDemoFlow ? 'demo_flow' : 'signup_flow',
        ...(utmParams ?? {}),
      });
      return response.data;
    },
    onSuccess: async (data, submittedData) => {
      setSignupId(data.signup_id);
      const skipToVerificationEmail = data.booked_demo || data.triaged;
      createPartnerStackSignup({ email: submittedData.email, customerKey: data.signup_id });

      try {
        // Submit to HubSpot with available data so far (in case user drops off early)
        await submitHubspotForm(isDemoFlow ? 'demo' : 'signup', {
          email: submittedData.email,
          source: isDemoFlow ? 'demo_flow' : 'signup_flow',
          utm_source: utmParams?.utm_source,
          utm_medium: utmParams?.utm_medium,
          utm_campaign: utmParams?.utm_campaign,
          utm_term: utmParams?.utm_term,
          utm_content: utmParams?.utm_content,
          booked_demo: data.booked_demo,
          google_ad_click_id: googleAdClickId,
        });
      } catch (err) {
        // Silently fail - this is just for tracking, shouldn't block the flow
        console.error('Failed to submit to HubSpot:', err);
      }

      onNext(skipToVerificationEmail);
    },
    onError: (error: any) => {
      console.error('Error starting signup flow:', error);
      const serverErrorMessage = handleServerError(error);
      setError(serverErrorMessage);
    },
  });

  const onSubmit = (newFormData: SignupFormData) => {
    setEmail(newFormData.email);
    submitSignup.mutate(newFormData);
  };

  return (
    <PageContainerWithLogo className={className} data-fs-unmask="true">
      <ProgressCard progressBar={{ current: 1, total: 4 }} desktopMinHeight={`${START_STEP_DESKTOP_MIN_HEIGHT}px`}>
        <CardContent>
          <CardSection>
            <SignupFlowContent variant={isDemoFlow ? 'demo' : 'default'} />
          </CardSection>
          <CardSubSection>
            <Controller
              control={control}
              name="email"
              rules={{
                required: { value: true, message: requiredErrorString },
                pattern: {
                  value: EMAIL_VALIDATION_REGEX,
                  message: 'Invalid email',
                },
              }}
              render={({ field }) => (
                <Input
                  label="Your Work Email"
                  type="email"
                  required
                  hasError={!!errors.email}
                  {...field}
                  onChange={(e) => {
                    field.onChange(e);
                    setError(null);
                    clearErrors('email');
                  }}
                  placeholder="name@example.com"
                  {...createTrackingProps({ unmask: true })}
                />
              )}
            />
            {errors.email && <ErrorMessage marginBottom="16px">{errors.email.message}</ErrorMessage>}
            {!errors.email && error && <SignupErrorMessage error={error} marginBottom="16px" />}
            <Button
              onClick={handleSubmit(onSubmit)}
              width="full"
              textStyledAs="bodySmallDMSans"
              textWeight={700}
              isLoading={submitSignup.isPending}
            >
              Get started
            </Button>
          </CardSubSection>
        </CardContent>
      </ProgressCard>
      <CertificationIcons />
    </PageContainerWithLogo>
  );
};

export default SignupFlowStart;
